import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  TextField,
  Chip,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import { SlotsFormValues } from "../types/types";

interface LocationProps {
  dropLocation?: string[];
  pickupLocation?: string[];
  onChange: (field: keyof SlotsFormValues, value: any) => void;
  editable?: boolean;
}

const Location: React.FC<LocationProps> = ({
  dropLocation = [],
  pickupLocation = [],
  onChange,
  editable = true,
}) => {
  const [tempDrop, setTempDrop] = React.useState("");
  const [tempPickup, setTempPickup] = React.useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleAddLocation = (type: "dropLocation" | "pickupLocation") => {
    const value = type === "dropLocation" ? tempDrop.trim() : tempPickup.trim();
    const currentLocations =
      type === "dropLocation" ? dropLocation : pickupLocation;

    if (value && currentLocations.length < 10) {
      const updated = [...currentLocations, value];
      onChange(type, updated);
      type === "dropLocation" ? setTempDrop("") : setTempPickup("");
    }
  };

  const handleRemoveLocation = (
    type: "dropLocation" | "pickupLocation",
    index: number
  ) => {
    const currentLocations =
      type === "dropLocation" ? dropLocation : pickupLocation;
    if (currentLocations.length > 1) {
      const updated = currentLocations.filter((_, i) => i !== index);
      onChange(type, updated);
    }
  };

  return (
    <Box
      sx={{
        mt: { xs: 5, sm: 4 },
        position: "relative",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 4, sm: 2 },
        padding: { xs: "28px 16px 16px", sm: "22px" },
        borderRadius: { xs: "20px", sm: "30px" },
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        transition: "all 0.2s ease",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Typography
        sx={{
          position: "absolute",
          top: { xs: "-12px", sm: "-18px" },
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#F5F7FA",
          padding: "2px 10px",
          borderRadius: "20px",
          fontWeight: 600,
          fontSize: { xs: "16px", sm: "18px" },
          textAlign: "center",
          whiteSpace: { xs: "normal", sm: "nowrap" },
          maxWidth: { xs: "90%", sm: "auto" },
        }}
      >
        Boarding/Drop-off Point
      </Typography>
      
      {/* Pickup Location Section */}
      <Box width={{ xs: "100%", sm: "50%" }} mb={{ xs: 2, sm: 0 }}>
        <Typography 
          variant="body1" 
          color="default" 
          fontWeight={400} 
          fontSize={14}
          sx={{
            mb: 1,
            display: "block",
          }}
        >
          Sample: Karachi Bakery, Indiranagar, 21:00
        </Typography>
        <TextField
          fullWidth
          placeholder="Boarding Point"
          value={tempPickup}
          onChange={(e) => setTempPickup(e.target.value)}
          disabled={!editable || pickupLocation.length >= 10}
          onKeyPress={(e) =>
            e.key === "Enter" && handleAddLocation("pickupLocation")
          }
          size={isMobile ? "small" : "medium"}
          InputProps={{
            endAdornment: editable && (
              <IconButton
                onClick={() => handleAddLocation("pickupLocation")}
                disabled={pickupLocation.length >= 10 || !tempPickup.trim()}
                size={isMobile ? "small" : "medium"}
              >
                <AddIcon />
              </IconButton>
            ),
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ 
            mt: 1, 
            flexWrap: "wrap", 
            gap: 1,
            maxWidth: "100%",
            overflow: "hidden"
          }}
        >
          {pickupLocation.map((loc, index) => (
            <Chip
              key={index}
              label={loc}
              onDelete={
                editable && pickupLocation.length > 1
                  ? () => handleRemoveLocation("pickupLocation", index)
                  : undefined
              }
              sx={{
                backgroundColor: "#b6d1c4",
                color: "#333",
                borderRadius: "15px",
                fontSize: { xs: "12px", sm: "14px" },
                fontFamily: 'Inter',
                fontWeight: 500,
                maxWidth: "100%",
                height: "auto",
                margin: "2px",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  overflow: "visible",
                  padding: { xs: "4px 6px", sm: "6px 8px" },
                },
                "& .MuiChip-deleteIcon": {
                  margin: { xs: "0 2px", sm: "0 4px" },
                },
              }}
              deleteIcon={<CloseIcon fontSize={isMobile ? "small" : "medium"} />}
            />
          ))}
        </Stack>
      </Box>
      
      {/* Drop Location Section */}
      <Box width={{ xs: "100%", sm: "50%" }}>
        <Typography 
          variant="body1" 
          color="default" 
          fontWeight={400} 
          fontSize={14}
          sx={{
            mb: 1,
            display: "block",
          }}
        >
          Sample: Karachi Bakery, Indiranagar, 21:00
        </Typography>
        <TextField
          fullWidth
          placeholder="Drop-off Point"
          value={tempDrop}
          onChange={(e) => setTempDrop(e.target.value)}
          disabled={!editable || dropLocation.length >= 10}
          onKeyPress={(e) =>
            e.key === "Enter" && handleAddLocation("dropLocation")
          }
          size={isMobile ? "small" : "medium"}
          InputProps={{
            endAdornment: editable && (
              <IconButton
                onClick={() => handleAddLocation("dropLocation")}
                disabled={dropLocation.length >= 10 || !tempDrop.trim()}
                size={isMobile ? "small" : "medium"}
              >
                <AddIcon />
              </IconButton>
            ),
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ 
            mt: 1, 
            flexWrap: "wrap", 
            gap: 1,
            maxWidth: "100%",
            overflow: "hidden"
          }}
        >
          {dropLocation.map((loc, index) => (
            <Chip
              key={index}
              label={loc}
              onDelete={
                editable && dropLocation.length > 1
                  ? () => handleRemoveLocation("dropLocation", index)
                  : undefined
              }
              sx={{
                backgroundColor: "#b6d1c4",
                color: "#333",
                borderRadius: "15px",
                fontSize: { xs: "12px", sm: "14px" },
                fontFamily: 'Inter',
                fontWeight: 500,
                maxWidth: "100%",
                height: "auto",
                margin: "2px",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  overflow: "visible",
                  padding: { xs: "4px 6px", sm: "6px 8px" },
                },
                "& .MuiChip-deleteIcon": {
                  margin: { xs: "0 2px", sm: "0 4px" },
                },
              }}
              deleteIcon={<CloseIcon fontSize={isMobile ? "small" : "medium"} />}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default Location;